import { Button, Modal } from '@restomarker/ui-kit';
import type { ReactElement } from 'react';

import { useLocations } from '@/modules/locations';
import { NetworkError } from '@/shared/api';
import { useSupportSession } from '@/shared/auth';
import { useOnlineStatus } from '@/shared/hooks';
import { t } from '@/shared/i18n';
import { systemNotificationSupport } from '@/shared/lib/systemNotification';
import { useSession } from '@/shared/session';

import { isLegacy, useAlertSettings } from './api';
import { ChannelSections } from './ChannelSections';
import { DeviceNotificationsSection } from './feed/DeviceNotificationsSection';
import { useLocationFeedSettings } from './feed/api/useLocationFeedSettings';
import { LocationFeedSection } from './feed/LocationFeedSection';
import { LegacySections } from './LegacySections';
import styles from './notificationsDialog.module.css';

export interface NotificationsDialogProps {
  onClose: () => void;
  /** Точка ленты, чьи пороги показать (T-047); по умолчанию — основная точка человека. */
  location?: string | null;
}

/**
 * Уведомления: утренняя сводка по срокам (п.11) и предупреждения заранее по каждой
 * заготовке (решение владельца 2026-09-26, «всем, кто включил») — в Telegram, push
 * на телефон или туда и туда, на выбор человека. Когда предупреждать — у каждого
 * канала свой набор порогов (T-046).
 *
 * Что показывать, решает СЕРВЕРНОЕ состояние (`alert-settings`), а не ключ на телефоне:
 * галочка push, нарисованная по локальному ключу, и была тем, что «слетало» (B-039).
 * Окно спрашивает сервер при каждом открытии. Настройки меняются только онлайн. Без
 * сети окно показывает последние известные и одну подпись над разделами; без данных
 * и без сети — только подпись. Сервер без alert-settings (404) — окно как до T-046
 * (`LegacySections`).
 *
 * Два блока с заголовками (№12, 2026-09-30): «Для всей точки» — уведомления Windows
 * этого устройства и пороги ленты точки, «Лично мне» — Telegram и push. Раньше разделы
 * шли одним списком, и две сетки «Когда предупреждать» подряд владелец принял за
 * задублированное окно, хотя одна — его личная, а другая — общая на точку.
 */
export function NotificationsDialog({
  onClose,
  location: chosen,
}: NotificationsDialogProps): ReactElement {
  const settings = useAlertSettings({ fresh: true });
  const online = useOnlineStatus();
  const supportSession = useSupportSession() !== null;
  // Сводка — руководству точки; повару она не приходит, и обещать её ему незачем.
  const user = useSession().data;
  const role = user?.role;
  // Лента точки (T-047): основная точка человека или выбранная на экране ленты.
  const feedLocation =
    role === 'platform_admin' ? null : (chosen ?? user?.default_location ?? null);
  // Блок «Для всей точки» — когда в нём что-то есть: Windows-оболочка или лента точки.
  const feedSettings = useLocationFeedSettings(feedLocation).data;
  const hasFeed = Boolean(feedSettings && Array.isArray(feedSettings.feed_tiers));
  const hasDevice = systemNotificationSupport() !== 'none';
  const feedLocationName =
    useLocations().data?.results?.find((item) => item.id === feedLocation)?.name ?? '';
  const getsDigest = role === 'location_manager' || role === 'client_admin';
  // Последняя загрузка не дошла — это тоже «нет сети»: `navigator.onLine` на кухонном
  // WiFi врёт регулярно (`useSync.ts`). Ошибка живёт в запросе до следующего УСПЕШНОГО
  // ответа, и раньше, когда переспрашивать было некому, окно держало «нет сети»
  // и после того, как связь вернулась (ревью B-039). Теперь переспрашивают открытие
  // окна, повтор раз в 30 с (`useAlertSettings`) и «Повторить» под подписью.
  const unreachable = settings.error instanceof NetworkError;
  const offline = !online || unreachable;
  // Саппорт-сессия смотрит чужие настройки: только чтение, запись всё равно получит 403.
  const readOnly = offline || supportSession;
  const data = settings.data;
  const retry = (): void => {
    void settings.refetch();
  };

  let body: ReactElement | null;
  if (data !== undefined && isLegacy(data)) {
    body = <LegacySections disabled={readOnly} />;
  } else if (data !== undefined) {
    body = <ChannelSections settings={data} disabled={readOnly} supportSession={supportSession} />;
  } else if (offline) {
    body = null;
  } else if (settings.isError) {
    body = (
      <div className={styles.body}>
        <p className={styles.problem} role="alert">
          {t('alerts.loadFailed')}
        </p>
        <Button onClick={retry}>{t('common.retry')}</Button>
      </div>
    );
  } else {
    // До первого ответа галочек нет вовсе: выключенная галочка до ответа читалась бы
    // ложным «выключено» — ровно тем, что человек и принимал за «слетело».
    body = <p className={styles.muted}>{t('common.loading')}</p>;
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title={t('notifications.title')}
      description={t(getsDigest ? 'notifications.description' : 'notifications.descriptionStaff')}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <div className={styles.sections}>
        {hasDevice || hasFeed ? (
          <div className={styles.group} role="group" aria-labelledby="notifications-location">
            <div className={styles.groupHead}>
              <h2 id="notifications-location" className={styles.groupTitle}>
                {t('notifications.group.location')}
              </h2>
              <p className={styles.muted}>{t('notifications.group.locationHint')}</p>
            </div>
            {/* Уведомления Windows — первыми: на планшете это главное (T-047). */}
            <DeviceNotificationsSection disabled={supportSession} />
            {feedLocation !== null ? (
              <LocationFeedSection
                location={feedLocation}
                locationName={feedLocationName}
                disabled={readOnly}
              />
            ) : null}
          </div>
        ) : null}
        <div className={styles.group} role="group" aria-labelledby="notifications-mine">
          <div className={styles.groupHead}>
            <h2 id="notifications-mine" className={styles.groupTitle}>
              {t('notifications.group.mine')}
            </h2>
            <p className={styles.muted}>{t('notifications.group.mineHint')}</p>
          </div>
          {offline ? (
            <div className={styles.body}>
              <p className={styles.offline} role="status">
                {t('alerts.offline')}
              </p>
              {/* Запрос не дошёл, а `navigator.onLine` мог и соврать: переспросить сервер
                  можно, не дожидаясь повтора раз в 30 с. */}
              {unreachable ? (
                <Button variant="secondary" disabled={settings.isFetching} onClick={retry}>
                  {t('common.retry')}
                </Button>
              ) : null}
            </div>
          ) : null}
          {body}
        </div>
      </div>
    </Modal>
  );
}
